import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  AddressResolver,
  AuctionBidsResolver,
  AuctionResolver,
  AuthGuard,
  BalanceResolver,
  CardTokenResolver,
  CountryResolver,
  firstTimeUserGuard,
  LottoResolver,
  LottoWalletResolver,
  MaintenanceGuard,
  MemberCardResolver,
  MemberLoyaltyStatusResolver,
  MemberResolver,
  PreviewResolver,
  PurchaseResolver,
  SeatingDetailResolver,
  TransactionSuccessGuard,
} from '@fgb/core';

// General

import { HomePageComponent } from './pages/home-page/home-page.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';

// Unauthenticated
import { AuthCallbackComponent } from './modules/user/components/auth/auth-callback/auth-callback.component';
import { SingleSignOnAppComponent } from './modules/user/components/single-sign-on-app/single-sign-on-app.component';
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';

// Registration
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
import { TmCallbackComponent } from './shared/components/manage-tickets/tm-callback/tm-callback.component';
import { TmAuthCallbackComponent } from './shared/components/providers/tm/tm-auth-callback/tm-auth-callback.component';
import { ScorecardDescriptionComponent } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { RewardsLayoutComponent } from './pages/features/rewards-section/rewards-layout/rewards-layout.component';
import { RewardsPageComponent } from './pages/features/rewards-section/rewards-page/rewards-page.component';
import { AuctionsPageComponent } from './pages/features/rewards-section/auctions-page/auctions-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/features/rewards-section/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { LottosPageComponent } from './pages/features/rewards-section/lottos-page/lottos-page.component';
import { LottoDetailPageComponent } from './pages/features/rewards-section/lotto-detail-page/lotto-detail-page.component';
import { LottosSuccessPageComponent } from './pages/features/rewards-section/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplaceWalletPageComponent } from './pages/features/wallet-section/marketplace-wallet-page/marketplace-wallet-page.component';
import { LottoWalletDetailsPageComponent } from './pages/features/wallet-section/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { AuctionWalletDetailsPageComponent } from './pages/features/wallet-section/auction-wallet-details-page/auction-wallet-details-page.component';
import { WalletClaimedPageComponent } from './pages/features/wallet-section/wallet-claimed-page/wallet-claimed-page.component';
import { EcashPageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-page.component';
import { EcashSuccessPageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-success-page/ecash-success-page.component';
import { EcashFailurePageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashResultPageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashHistoryPageComponent } from './pages/features/jets-cash-section/ecash-history/ecash-history.component';
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { BadgesPageComponent } from './pages/features/challenges-section/badges-page/badges-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/features/jets-cash-section/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { CartCheckoutResultsPageComponent } from './pages/features/jets-cash-section/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { EarnPageComponent } from './pages/earn-page/earn-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
import { CartCheckoutPageComponent } from './pages/features/jets-cash-section/cart-checkout-page/cart-checkout-page.component';
import { MarketplaceDetailPageComponent } from './pages/features/rewards-section/marketplace-detail-page/marketplace-detail-page.component';
import { WalletPageComponent } from './pages/features/wallet-section/wallet-page/wallet-page.component';
import { AccountDetailsPageComponent } from './pages/features/account-section/account-details-page/account-details-page.component';
import { ActivityPageComponent } from './pages/features/account-section/account-details-page/activity-page/activity-page.component';
import { AuctionDetailPageComponent } from './pages/features/rewards-section/auction-detail-page/auction-detail-page.component';

/* ========================= */
/* 🔁 Shared Resolve Groups */
/* ========================= */

const baseUserResolvers = {
  member: MemberResolver,
  memberCard: MemberCardResolver,
  balances: BalanceResolver,
};

const paymentResolvers = {
  balances: BalanceResolver,
  cardTokens: CardTokenResolver,
  country: CountryResolver,
};

const walletResolvers = {
  purchases: PurchaseResolver,
  lottoWalletItems: LottoWalletResolver,
  auctionBids: AuctionBidsResolver,
};

/* ========================= */
/* 🧭 Routes */
/* ========================= */

const routes: Routes = [
  /* ========================= */
  /* 🔐 Authenticated App */
  /* ========================= */
  {
    path: '',
    component: MasterPageComponent,
    canActivate: [AuthGuard, firstTimeUserGuard()],
    resolve: baseUserResolvers,
    children: [
      { path: '', component: HomePageComponent, resolve: { cardTokens: CardTokenResolver } },

      /* ===== Rewards ===== */
      {
        path: 'rewards',
        component: RewardsLayoutComponent,
        children: [
          { path: '', component: RewardsPageComponent },

          /* Auctions */
          {
            path: 'auctions',
            children: [
              {
                path: '',
                component: AuctionsPageComponent,
                resolve: { auctions: AuctionResolver },
              },
              {
                path: 'details/:id',
                component: AuctionDetailPageComponent,
                resolve: { ...baseUserResolvers, auctions: AuctionResolver },
              },
            ],
          },

          /* Marketplace */
          {
            path: 'marketplace',
            children: [
              { path: '', redirectTo: '/rewards', pathMatch: 'full' },
              { path: '', component: RewardsPageComponent },

              {
                path: 'details/:id',
                component: MarketplaceDetailPageComponent,
                resolve: paymentResolvers,
              },
              {
                path: 'purchase/success',
                component: MarketplacePurchaseSuccessPageComponent,
                canActivate: [TransactionSuccessGuard],
                resolve: {
                  balances: BalanceResolver,
                  purchase: PurchaseResolver,
                },
              },
            ],
          },

          /* Raffles (Lotto) */
          {
            path: 'raffles',
            children: [
              {
                path: '',
                component: LottosPageComponent,
                resolve: { lottos: LottoResolver, balances: BalanceResolver },
              },
              {
                path: 'details/:id',
                component: LottoDetailPageComponent,
                resolve: { lottos: LottoResolver, balances: BalanceResolver },
              },
              {
                path: 'purchase/success',
                component: LottosSuccessPageComponent,
                canActivate: [TransactionSuccessGuard],
              },
            ],
          },

          /* Wallet */
          {
            path: 'wallet',
            children: [
              {
                path: '',
                component: WalletPageComponent,
                resolve: { cardTokens: CardTokenResolver },
              },
              {
                path: 'details/:id',
                component: MarketplaceWalletPageComponent,
                resolve: { purchases: PurchaseResolver },
              },
              {
                path: 'raffledetails/:id',
                component: LottoWalletDetailsPageComponent,
                resolve: { lottoWalletItems: LottoWalletResolver },
              },
            ],
          },

          /* Claimed */
          {
            path: 'claimed',
            component: WalletClaimedPageComponent,
            resolve: walletResolvers,
          },

          /* Bids */
          {
            path: 'bids/details/:id',
            component: AuctionWalletDetailsPageComponent,
            resolve: { bids: AuctionBidsResolver },
          },
        ],
      },

      /* ===== Scorecards ===== */
      { path: 'challenges', component: BadgesPageComponent },

      /* ===== Account ===== */
      {
        path: 'account',
        component: AccountDetailsPageComponent,
        resolve: {
          ...baseUserResolvers,
          seating: SeatingDetailResolver,
          memberLoyaltyStatus: MemberLoyaltyStatusResolver,
          cardTokens: CardTokenResolver,
        },
      },
      { path: 'activity', component: ActivityPageComponent },

      /* ===== Tertiary Pages ===== */
      { path: 'scorecard-description/:id', component: ScorecardDescriptionComponent },
      { path: 'contact', component: ContactUsPageComponent },
      { path: 'faq', component: FaqPageComponent },
      { path: 'terms', component: TermsPageComponent },
      { path: 'earn', component: EarnPageComponent },

      /* ===== eCash ===== */
      {
        path: 'ecash',
        resolve: paymentResolvers,
        children: [
          { path: '', component: EcashPageComponent },
          { path: 'success', component: EcashSuccessPageComponent, resolve: { balances: BalanceResolver } },
          { path: 'failure', component: EcashFailurePageComponent },
          { path: 'result', component: EcashResultPageComponent },
        ],
      },

      {
        path: 'ecashhistory',
        component: EcashHistoryPageComponent,
        resolve: {
          ...baseUserResolvers,
          cardTokens: CardTokenResolver,
        },
      },

      /* ===== Checkout ===== */
      {
        path: 'checkout',
        resolve: { address: AddressResolver },
        children: [
          {
            path: '',
            component: CartCheckoutPageComponent,
            resolve: {
              member: MemberResolver,
              address: AddressResolver,
              country: CountryResolver,
            },
          },
          { path: 'success', component: CartCheckoutSuccessPageComponent },
          { path: 'results', component: CartCheckoutResultsPageComponent },
        ],
      },

      /* ===== Misc ===== */
      { path: 'signin-ticketmaster', component: TmCallbackComponent },
      { path: '404page', component: NotFoundPageComponent },
    ],
  },

  /* ========================= */
  /* 🔐 Public Routes */
  /* ========================= */

  { path: '', redirectTo: 'login', pathMatch: 'full' },
  {
    path: 'login',
    component: LoginPageComponent,
    resolve: { preview: PreviewResolver },
    canActivate: [MaintenanceGuard],
  },
  { path: 'login/accounts', component: LoginAccountsPageComponent },

  /* ===== Registration ===== */
  {
    path: 'register',
    component: RegistrationLayoutPageComponent,
    loadChildren: () => import('@fgb/portal-component-library/src/lib/registration').then((m) => m.RegistrationModule),
  },

  /* ===== Auth Callbacks ===== */
  { path: 'callback', component: AuthCallbackComponent },
  { path: 'tm-auth-callback', component: TmAuthCallbackComponent },

  /* ===== SSO ===== */
  { path: 'loginApp', component: SingleSignOnAppComponent },
  { path: 'loginApp/:providerId', component: SingleSignOnAppComponent },
  { path: 'loginApp/:providerId/:clubId', component: SingleSignOnAppComponent },
  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId/:clubId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },

  /* ===== Maintenance ===== */
  { path: 'maintenance', component: MaintenancePageComponent },

  /* ===== Fallback ===== */
  { path: '**', redirectTo: '404page' },
];

/* ========================= */
/* 🚀 Module */
/* ========================= */

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
